import { useEffect, useState } from 'react';
import { useOp } from '../api.ts';
import { Link, useRouter } from '../router.tsx';
import type { CouponList } from '../types.ts';
import { useChanges } from '../ui/changes.ts';
import { step, useKeys } from '../ui/keys.ts';
import { Empty, ErrorBox, Loading, STATE_LABELS, StateBadge, relativeDays } from '../ui/kit.tsx';
import { SearchBox } from '../ui/SearchBox.tsx';
import { CouponBulkBar } from './catalog/routes.tsx';

/** Above this many sites the per-site columns collapse into one summary column. */
const MAX_SITE_COLUMNS = 4;

export function Coupons() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const q = query.get('q') ?? '';
  const kind = query.get('kind') ?? '';
  const site = query.get('site') ?? '';
  const state = query.get('state') ?? '';
  const expiring = query.get('expiring') ?? '';
  const includeExpired = query.get('expired') === '1';
  const input = {
    q: q || undefined,
    kind: kind || undefined,
    propertyRef: site || undefined,
    state: state || undefined,
    expiringWithinDays: expiring ? Number(expiring) : undefined,
    includeExpired,
    limit: 100,
  };
  const list = useOp<CouponList>('admin.coupons.list', input, changes);
  const rows = list.data?.rows ?? [];
  const sites = list.data?.sites ?? [];
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => setCursor(0), [q, kind, site, state, expiring, includeExpired]);
  const toggle = (ref: string) => setSelected((s) => (s.includes(ref) ? s.filter((r) => r !== ref) : [...s, ref]));

  const open = (index: number) => rows[index] && setQuery({ coupon: rows[index]!.ref });
  useKeys({
    j: () => setCursor((c) => step(c, 1, rows.length)),
    k: () => setCursor((c) => step(c, -1, rows.length)),
    x: () => rows[cursor] && toggle(rows[cursor]!.ref),
    enter: () => open(cursor),
    o: () => open(cursor),
  });

  const perSiteColumns = sites.length <= MAX_SITE_COLUMNS;
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Coupons</h1>
          <p className="muted">{list.data ? `${list.data.total} in ${list.data.market}${expiring ? ` expiring within ${expiring} days` : ''}` : ' '}</p>
        </div>
        <Link to="/coupons/new" className="btn btn-primary">
          New coupon
        </Link>
      </header>
      <div className="toolbar">
        <SearchBox value={q} onChange={(value) => setQuery({ q: value || null }, { replace: true })} placeholder="Search title, code or merchant" />
        <label className="inline-field">
          Kind
          <select value={kind} onChange={(e) => setQuery({ kind: e.target.value || null }, { replace: true })}>
            <option value="">Codes and deals</option>
            <option value="code">Codes</option>
            <option value="deal">Deals</option>
          </select>
        </label>
        <label className="inline-field">
          State
          <select value={state} onChange={(e) => setQuery({ state: e.target.value || null }, { replace: true })}>
            <option value="">Any</option>
            {Object.entries(STATE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
            <option value="not_offered">Not offered</option>
          </select>
        </label>
        <label className="inline-field">
          on
          <select value={site} onChange={(e) => setQuery({ site: e.target.value || null }, { replace: true })}>
            <option value="">any of my sites</option>
            {sites.map((s) => (
              <option key={s.ref} value={s.ref}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={expiring === '7'} onChange={(e) => setQuery({ expiring: e.target.checked ? '7' : null }, { replace: true })} />
          Expiring in 7 days
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={includeExpired} onChange={(e) => setQuery({ expired: e.target.checked ? '1' : null }, { replace: true })} />
          Include expired
        </label>
      </div>
      <CouponBulkBar selected={selected} sites={sites} onDone={() => setSelected([])} />
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No coupons match these filters.</Empty> : null}
      {rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col" className="check">
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  checked={rows.length > 0 && rows.every((r) => selected.includes(r.ref))}
                  onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.ref) : [])}
                />
              </th>
              <th scope="col">Merchant</th>
              <th scope="col">Title</th>
              <th scope="col">Code</th>
              <th scope="col">Ends</th>
              {perSiteColumns ? (
                sites.map((s) => (
                  <th scope="col" key={s.ref}>
                    {s.name}
                  </th>
                ))
              ) : (
                <th scope="col">Sites</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => open(index)} onMouseEnter={() => setCursor(index)}>
                <td className="check" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Select ${row.title}`} checked={selected.includes(row.ref)} onChange={() => toggle(row.ref)} />
                </td>
                <td className="muted">{row.merchant.name}</td>
                <td>
                  <button type="button" className="link-button" onClick={() => open(index)}>
                    {row.title}
                  </button>
                  <small className="muted block">
                    {row.discount}
                    {row.sourceType !== 'demo_seed' && row.sourceType !== 'manual' ? ` · ${row.sourceType}` : ''}
                  </small>
                </td>
                <td>{row.code ? <code>{row.code}</code> : <span className="muted">deal</span>}</td>
                <td title={row.endsAt ?? ''}>{relativeDays(row.endsAt)}</td>
                {perSiteColumns ? (
                  row.sites.map((s) => (
                    <td key={s.propertyRef}>
                      <StateBadge state={s.state} live={s.live} />
                    </td>
                  ))
                ) : (
                  <td>
                    {row.sites.filter((s) => s.live).length} live · {row.sites.filter((s) => s.state === 'pending_review').length} in review
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
